import { useEffect, useState } from "react";
import {
  PRIVACY_NOTICE_CONFIG,
  PRIVACY_NOTICE_EXCLUDED_COUNTRIES,
} from "~/config/navigation";
import { getVisitorCountry } from "~/utils/geo-fns";
import { Close } from "~/components/icons";

/**
 * Privacy notice banner (bottom-left card).
 *
 * Shown to visitors whose country is NOT covered by the CookieYes consent
 * gate (EU-27 + UK, loaded via GTM) — those regions get CookieYes instead.
 * This is a *notice*, not a consent gate: cookies are already set for these
 * regions, so it informs and links to the policy rather than asking to opt in.
 *
 * Renders nothing on the server and on first client paint, then resolves
 * geo after mount. Rendering client-side only is deliberate: every page is
 * ISR-cached and served to all regions, so the show/hide decision can never
 * live in the cached HTML.
 *
 * The resolved country is cached in localStorage for a day so the geo
 * server function runs at most ~once per visitor per day — NOT on every
 * pageview (and not again for ACK_TTL_MS once the notice is dismissed).
 */

const GEO_CACHE_KEY = "c1_geo_v1";
const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Dismissal is remembered for a bounded window, not forever — legal guidance
// is to avoid permanently suppressing the notice after a single dismissal
// (courts have questioned that on shared devices), but re-showing on every
// single browser session is more than the client wants. 30 days splits it.
const ACK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** True if the notice was dismissed within the last ACK_TTL_MS. */
function isAcknowledged(): boolean {
  try {
    const raw = localStorage.getItem(PRIVACY_NOTICE_CONFIG.storageKey);
    if (!raw) return false;
    const t = Number(raw);
    if (!Number.isFinite(t)) return false;
    return Date.now() - t <= ACK_TTL_MS;
  } catch {
    return false;
  }
}

function writeAcknowledged() {
  try {
    localStorage.setItem(PRIVACY_NOTICE_CONFIG.storageKey, String(Date.now()));
  } catch {
    // Nothing we can do; the banner still closes for this session.
  }
}

/** Read the cached country. Returns null when absent, stale, or unreadable. */
function readCachedCountry(): string | null {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const { c, t } = JSON.parse(raw) as { c?: string; t?: number };
    if (typeof c !== "string" || typeof t !== "number") return null;
    if (Date.now() - t > GEO_CACHE_TTL_MS) return null;
    return c;
  } catch {
    return null;
  }
}

function writeCachedCountry(country: string) {
  try {
    localStorage.setItem(
      GEO_CACHE_KEY,
      JSON.stringify({ c: country, t: Date.now() }),
    );
  } catch {
    // localStorage unavailable — we'll just re-resolve next pageview.
  }
}

export function PrivacyNotice() {
  // `mounted` gates DOM presence; `open` drives the slide-in transition.
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!PRIVACY_NOTICE_CONFIG.enabled) return;

    // Already dismissed within the acknowledgement window — don't show again yet.
    if (isAcknowledged()) return;

    let cancelled = false;

    const decide = (country: string) => {
      if (cancelled) return;
      // Fail safe: with no country (rare in prod, always local dev) we
      // can't rule out an EU visitor, so stay hidden in production to
      // avoid a double banner. In dev, show it so the UI is previewable.
      const show = country
        ? !PRIVACY_NOTICE_EXCLUDED_COUNTRIES.has(country)
        : import.meta.env.DEV;
      if (!show) return;
      setMounted(true);
      // Next frame so the initial (translated-down) state paints first
      // and the slide-in transition actually runs.
      requestAnimationFrame(() => setOpen(true));
    };

    const cached = readCachedCountry();
    if (cached !== null) {
      decide(cached);
    } else {
      getVisitorCountry()
        .then(({ country }) => {
          // Only cache a real answer; an empty country (missing header)
          // shouldn't suppress the lookup for a whole day.
          if (country) writeCachedCountry(country);
          decide(country);
        })
        .catch(() => {
          // Geo lookup failed — fail closed, show nothing.
        });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss() {
    setOpen(false);
    writeAcknowledged();
    // Unmount after the slide-out transition completes.
    window.setTimeout(() => setMounted(false), 400);
  }

  if (!mounted) return null;

  // Bottom-LEFT corner card. On desktop it sits opposite the Chili Piper
  // chat launcher (bottom-right). On mobile it spans the width and may
  // overlap the launcher's corner. The z-index sits just BELOW the Chili
  // Piper widget (z-index 99999999 in production) on purpose: if a visitor
  // has the chat or booking flow open, that revenue path must always render
  // on top of this dismissible notice — never the other way around.
  return (
    <div
      role="region"
      aria-label="Privacy notice"
      className={`fixed bottom-4 left-4 right-4 z-[99999998] rounded-2xl border border-gray-20 bg-white text-charcoal shadow-card transition-all duration-300 ease-out sm:right-auto sm:w-[560px] ${
        open ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      }`}
    >
      <div className="flex items-start gap-3 p-5">
        <div className="flex-1">
          <p className="text-[15px] leading-relaxed text-charcoal">
            This website uses cookies and similar technologies to view and
            retain your site interactions, perform analytics, improve your
            experience, and assist our marketing efforts. See our{" "}
            <a
              href="/privacy-policy"
              className="text-teal-dark underline hover:text-teal-dark-hover"
            >
              Privacy Policy
            </a>{" "}
            for more details.
          </p>
        </div>
        {/* Below sm the notice is a full-width bar pinned to the bottom edge, and the
            close control sits on the LEFT there; from sm up it's a 560px corner card
            and it returns to the right. Same breakpoint that switches the shape, so
            the two can't disagree. `order-first` moves only this button rather than
            reversing the row, which leaves DOM order alone — and the negative margin has
            to flip with it, since it exists to pull the icon into the card's padding at
            whichever corner it occupies. Visible "Close" label (not just an X) so the
            control can't be read as an opt-in/opt-out choice — dismissing it is just
            closing the notice, not making a consent decision. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="order-first -ml-1.5 -mt-1.5 flex flex-shrink-0 flex-col items-center gap-0.5 rounded-md p-1.5 text-gray-80 transition-colors hover:bg-gray-10 hover:text-charcoal sm:order-none sm:ml-0 sm:-mr-1.5"
        >
          <Close className="h-5 w-5" />
          <span className="text-[10px] font-medium leading-none">Close</span>
        </button>
      </div>
    </div>
  );
}
